import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box } from '@interstate/components/Box';
import { Card } from '@interstate/components/Card';
import { Button } from '@interstate/components/Button';
import { FuelSolidIcon } from '@interstate/components/Icons';
import { PrinterIcon } from '@interstate/components/Icons/PrinterIcon';
import { PlusIcon } from '@interstate/components/Icons/PlusIcon';
import { openFuelSaving } from '@makemydeal/dr-activities-fuel-saving';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import * as priorBalanceSelectors from '@makemydeal/dr-dash-store/selectors/priorBalanceSelectors';

import { useDealSummary } from '../../../common/DealSummaryProvider';
import TradeItem from './TradeItem';
import TradesDetailedView from './TradesDetailedView';
import TradesHeader from './TradesHeader';
import { useTrades, type TradeInItemWithIndex } from './useTrades';
import { useTradeEditModal } from './useTradeEditModal';
import { useTrackedSelector } from '../../../telemetry/useTrackedSelector';

const Trades = () => {
    const [isExpanded, setIsExpanded] = useState(true);

    const { tradeIns } = useTrades();
    const { openTradeModal } = useTradeEditModal();
    const { showDetailedView } = useDealSummary();
    const dispatch = useDispatch();
    const isFuelSavingEnabled = useTrackedSelector(featureToggleSelectors.isFuelSavingAnalysisEnabled, {
        component: 'Trades',
        field: 'fuelSavingAnalysisEnabled'
    });
    const isPriorBalanceApplicable = useTrackedSelector(priorBalanceSelectors.isPriorBalanceApplicable, {
        component: 'Trades',
        field: 'priorBalanceApplicable'
    });

    const hasTradeIns = tradeIns.length > 0;
    const showFuelSavingButton = isFuelSavingEnabled && tradeIns.some((tradeIn) => !!tradeIn.tradeIn.vin?.length);

    if (showDetailedView) {
        return <TradesDetailedView />;
    }
    return (
        <Card data-testid="trades-section" sx={{ p: 1, '.card-media-header-content-container': { display: 'none' } }}>
            <TradesHeader
                isExpanded={isExpanded}
                onToggleExpanded={() => setIsExpanded(!isExpanded)}
                onEdit={() => openTradeModal('trade-in')}
                hasTradeIns={hasTradeIns}
            />

            {/* Trades Content */}
            {hasTradeIns && isExpanded && (
                <>
                    {tradeIns.map(({ tradeIn, originalIndex, isDeleted }: TradeInItemWithIndex) => (
                        <TradeItem
                            key={`trade-${originalIndex}`}
                            year={tradeIn.year}
                            make={tradeIn.make}
                            model={tradeIn.model}
                            originalIndex={originalIndex}
                            isDeleted={isDeleted}
                            source={tradeIn.source}
                        />
                    ))}
                </>
            )}

            {hasTradeIns && (
                <>
                    <Box sx={{ borderTop: '1px solid', borderColor: 'base.color.ink.muted', my: 1 }} />
                    <Button
                        buttonStyle="tertiary"
                        size="aux"
                        startIcon={<PrinterIcon />}
                        onClick={() => openTradeModal('recon-cost')}
                    >
                        Print Trade Evaluation
                    </Button>
                </>
            )}

            {showFuelSavingButton && (
                <>
                    <Button
                        buttonStyle="tertiary"
                        size="aux"
                        startIcon={<FuelSolidIcon />}
                        onClick={() => dispatch(openFuelSaving())}
                    >
                        Fuel Savings Analysis
                    </Button>
                </>
            )}

            {isPriorBalanceApplicable && (
                <Button
                    buttonStyle="tertiary"
                    size="aux"
                    startIcon={<PlusIcon />}
                    data-testid="add-prior-balance-button"
                    onClick={() => openTradeModal('prior-balance')}
                >
                    Add Prior Balance
                </Button>
            )}
        </Card>
    );
};

export default Trades;
